/**
 * Comprehensive crisis dataset for CLEAR Humanitarian System
 * Demo data representing various global humanitarian crises
 */

import { CrisisMarker, ResourceMarker, AffectedArea, EarthquakeData } from '../types/map';
import { CrisisStatus } from '../types/crisis';

// Comprehensive global crisis dataset
export const crisisData: CrisisMarker[] = [
  // Critical Severity (5)
  {
    id: '1',
    title: 'Ukraine Humanitarian Crisis',
    type: 'conflict',
    severity: 5,
    status: 'active',
    location: 'Kyiv, Ukraine',
    latitude: 50.4501,
    longitude: 30.5234,
    affected_population: 6000000,
    created_at: '2022-02-24T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Ongoing armed conflict has resulted in massive displacement, infrastructure damage, and critical humanitarian needs across multiple regions. Emergency response focuses on protection, shelter, food security, and medical assistance.',
    response_agencies: ['UNHCR', 'WFP', 'UNICEF', 'NRC', 'MSF', 'OCHA', 'WHO'],
    respondingOrganizations: ['UNHCR', 'WFP', 'UNICEF', 'NRC', 'MSF', 'OCHA', 'WHO', 'Red Cross', 'Save the Children'],
    impact: {
      casualties: {
        deaths: 12450,
        injured: 28300,
        missing: 3200
      },
      affected: {
        total: 6000000,
        displaced: 3800000,
        refugees: 2200000
      },
      infrastructure: {
        schools_damaged: 890,
        hospitals_damaged: 156,
        bridges_destroyed: 23
      }
    },
    isActive: true,
    hasActiveAlerts: true
  },
  {
    id: '2',
    title: 'Gaza Humanitarian Emergency',
    type: 'conflict',
    severity: 5,
    status: 'active',
    location: 'Gaza Strip',
    latitude: 31.3547,
    longitude: 34.3088,
    affected_population: 2300000,
    created_at: '2023-10-07T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Severe humanitarian crisis with critical shortages of food, water, fuel, and medical supplies. Massive displacement and infrastructure damage affecting entire population of Gaza Strip.',
    response_agencies: ['UNRWA', 'WHO', 'WFP', 'OCHA', 'MSF', 'UNICEF'],
    respondingOrganizations: ['UNRWA', 'WHO', 'WFP', 'OCHA', 'MSF', 'UNICEF', 'PRCS', 'ICRC'],
    impact: {
      casualties: {
        deaths: 8900,
        injured: 22400,
        missing: 1200
      },
      affected: {
        total: 2300000,
        displaced: 1900000,
        refugees: 0
      },
      infrastructure: {
        schools_damaged: 278,
        hospitals_damaged: 22,
        water_facilities_damaged: 18
      }
    },
    isActive: true,
    hasActiveAlerts: true
  },
  {
    id: '3',
    title: 'Turkey-Syria Earthquake',
    type: 'earthquake',
    severity: 5,
    status: 'stabilizing',
    location: 'Kahramanmaraş, Turkey',
    latitude: 37.5858,
    longitude: 36.9371,
    affected_population: 15000000,
    created_at: '2023-02-06T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Devastating 7.8 magnitude earthquake causing widespread destruction across southern Turkey and northern Syria. Focus now on recovery, reconstruction, and psychological support for survivors.',
    response_agencies: ['AFAD', 'UN OCHA', 'WHO', 'UNICEF', 'NRC', 'Turkish Red Crescent'],
    respondingOrganizations: ['AFAD', 'UN OCHA', 'WHO', 'UNICEF', 'NRC', 'Turkish Red Crescent', 'UNHCR', 'MSF', 'WFP'],
    impact: {
      casualties: {
        deaths: 55000,
        injured: 129000,
        missing: 2800
      },
      affected: {
        total: 15000000,
        displaced: 2800000,
        refugees: 0
      },
      infrastructure: {
        buildings_collapsed: 173000,
        hospitals_damaged: 45,
        schools_damaged: 2300
      }
    },
    isActive: false,
    hasActiveAlerts: false
  },
  
  // High Severity (4)
  {
    id: '4',
    title: 'Afghanistan Humanitarian Crisis',
    type: 'food_insecurity',
    severity: 4,
    status: 'active',
    location: 'Kabul, Afghanistan',
    latitude: 34.5553,
    longitude: 69.2075,
    affected_population: 28300000,
    created_at: '2021-08-15T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Ongoing humanitarian crisis with severe food insecurity',
    response_agencies: ['WFP', 'UNICEF', 'WHO', 'NRC'],
    isActive: true,
    hasActiveAlerts: true
  },
  {
    id: '5',
    title: 'Sudan Armed Conflict',
    type: 'conflict',
    severity: 4,
    status: 'active',
    location: 'Khartoum, Sudan',
    latitude: 15.5007,
    longitude: 32.5599,
    affected_population: 25000000,
    created_at: '2023-04-15T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Armed conflict between military factions causing displacement',
    response_agencies: ['UNHCR', 'WFP', 'WHO', 'OCHA'],
    isActive: true,
    hasActiveAlerts: true
  },
  {
    id: '6',
    title: 'Somalia Drought Crisis',
    type: 'drought',
    severity: 4,
    status: 'active',
    location: 'Mogadishu, Somalia',
    latitude: 2.0469,
    longitude: 45.3182,
    affected_population: 7100000,
    created_at: '2020-01-01T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Prolonged drought causing severe food insecurity and displacement',
    response_agencies: ['WFP', 'UNICEF', 'FAO', 'NRC'],
    isActive: true,
    hasActiveAlerts: true
  },
  {
    id: '7',
    title: 'Yemen Humanitarian Crisis',
    type: 'conflict',
    severity: 4,
    status: 'active',
    location: 'Sana\'a, Yemen',
    latitude: 15.3694,
    longitude: 44.1910,
    affected_population: 21600000,
    created_at: '2014-09-21T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Protracted conflict causing world\'s worst humanitarian crisis',
    response_agencies: ['WFP', 'UNICEF', 'WHO', 'UNHCR', 'NRC'],
    isActive: true,
    hasActiveAlerts: true
  },
  
  // Medium Severity (3)
  {
    id: '8',
    title: 'Pakistan Floods',
    type: 'flood',
    severity: 3,
    status: 'stabilizing',
    location: 'Karachi, Pakistan',
    latitude: 24.8607,
    longitude: 67.0011,
    affected_population: 33000000,
    created_at: '2022-06-01T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Devastating floods affecting one-third of the country',
    response_agencies: ['NDMA', 'UN OCHA', 'WFP', 'UNICEF'],
    isActive: false,
    hasActiveAlerts: false
  },
  {
    id: '9',
    title: 'Libya Political Crisis',
    type: 'conflict',
    severity: 3,
    status: 'active',
    location: 'Tripoli, Libya',
    latitude: 32.8872,
    longitude: 13.1913,
    affected_population: 1300000,
    created_at: '2011-02-17T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Political instability affecting humanitarian access',
    response_agencies: ['UNHCR', 'IOM', 'WHO', 'UNICEF'],
    isActive: true,
    hasActiveAlerts: false
  },
  {
    id: '10',
    title: 'Ethiopia Tigray Crisis',
    type: 'conflict',
    severity: 3,
    status: 'stabilizing',
    location: 'Mekelle, Ethiopia',
    latitude: 13.4967,
    longitude: 39.4753,
    affected_population: 5400000,
    created_at: '2020-11-04T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Post-conflict humanitarian needs in Tigray region',
    response_agencies: ['UN OCHA', 'WFP', 'UNICEF', 'WHO'],
    isActive: false,
    hasActiveAlerts: false
  },
  {
    id: '11',
    title: 'Bangladesh Rohingya Crisis',
    type: 'displacement',
    severity: 3,
    status: 'active',
    location: 'Cox\'s Bazar, Bangladesh',
    latitude: 21.4272,
    longitude: 92.0058,
    affected_population: 890000,
    created_at: '2017-08-25T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Protracted refugee crisis with 890,000 Rohingya refugees',
    response_agencies: ['UNHCR', 'WFP', 'UNICEF', 'IOM', 'NRC'],
    isActive: true,
    hasActiveAlerts: false
  },
  {
    id: '12',
    title: 'Madagascar Food Crisis',
    type: 'food_insecurity',
    severity: 3,
    status: 'active',
    location: 'Antananarivo, Madagascar',
    latitude: -18.8792,
    longitude: 47.5079,
    affected_population: 1350000,
    created_at: '2020-01-01T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Climate-induced food insecurity in southern Madagascar',
    response_agencies: ['WFP', 'UNICEF', 'FAO', 'WHO'],
    isActive: true,
    hasActiveAlerts: false
  },
  
  // Low Severity (2)
  {
    id: '13',
    title: 'Philippines Typhoon Response',
    type: 'cyclone',
    severity: 2,
    status: 'stabilizing',
    location: 'Manila, Philippines',
    latitude: 14.5995,
    longitude: 120.9842,
    affected_population: 2800000,
    created_at: '2023-10-20T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Recent typhoon affecting northern Philippines',
    response_agencies: ['NDRRMC', 'UN OCHA', 'UNICEF', 'WHO'],
    isActive: false,
    hasActiveAlerts: false
  },
  {
    id: '14',
    title: 'Venezuelan Migration Crisis',
    type: 'displacement',
    severity: 2,
    status: 'active',
    location: 'Caracas, Venezuela',
    latitude: 10.4806,
    longitude: -66.9036,
    affected_population: 7300000,
    created_at: '2015-01-01T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Ongoing migration crisis affecting Latin America',
    response_agencies: ['UNHCR', 'IOM', 'WFP', 'UNICEF'],
    isActive: true,
    hasActiveAlerts: false
  },
  {
    id: '15',
    title: 'Kenya Drought Alert',
    type: 'drought',
    severity: 2,
    status: 'emerging',
    location: 'Nairobi, Kenya',
    latitude: -1.2921,
    longitude: 36.8219,
    affected_population: 4400000,
    created_at: '2023-08-01T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Emerging drought conditions in northern Kenya',
    response_agencies: ['NDMA', 'WFP', 'FAO', 'UNICEF'],
    isActive: true,
    hasActiveAlerts: true
  },
  
  // Minimal Severity (1)
  {
    id: '16',
    title: 'Indonesia Volcanic Activity',
    type: 'other',
    severity: 1,
    status: 'emerging',
    location: 'Jakarta, Indonesia',
    latitude: -6.2088,
    longitude: 106.8456,
    affected_population: 50000,
    created_at: new Date(Date.now() - 86400000).toISOString(),
    updated_at: new Date().toISOString(),
    description: 'Increased volcanic activity requiring monitoring',
    response_agencies: ['BNPB', 'WHO', 'UNICEF'],
    isActive: true,
    hasActiveAlerts: true
  },
  {
    id: '17',
    title: 'Brazil Floods',
    type: 'flood',
    severity: 1,
    status: 'resolved',
    location: 'São Paulo, Brazil',
    latitude: -23.5505,
    longitude: -46.6333,
    affected_population: 120000,
    created_at: '2023-11-01T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Seasonal flooding now under control',
    response_agencies: ['Defesa Civil', 'UN OCHA'],
    isActive: false,
    hasActiveAlerts: false
  },
  {
    id: '18',
    title: 'Morocco Earthquake Recovery',
    type: 'earthquake',
    severity: 1,
    status: 'stabilizing',
    location: 'Marrakech, Morocco',
    latitude: 31.6295,
    longitude: -7.9811,
    affected_population: 300000,
    created_at: '2023-09-08T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Recovery phase following September earthquake',
    response_agencies: ['Ministry of Interior', 'UN OCHA', 'WHO'],
    isActive: false,
    hasActiveAlerts: false
  },
  {
    id: '19',
    title: 'Chile Wildfire Season',
    type: 'other',
    severity: 1,
    status: 'active' as CrisisStatus,
    location: 'Santiago, Chile',
    latitude: -33.4489,
    longitude: -70.6693,
    affected_population: 85000,
    created_at: '2023-12-01T00:00:00Z',
    updated_at: new Date().toISOString(),
    description: 'Wildfire season monitoring and prevention',
    response_agencies: ['CONAF', 'ONEMI'],
    isActive: true,
    hasActiveAlerts: false
  },
  {
    id: '20',
    title: 'Australia Cyclone Watch',
    type: 'cyclone',
    severity: 1,
    status: 'active' as CrisisStatus,
    location: 'Darwin, Australia',
    latitude: -12.4634,
    longitude: 130.8456,
    affected_population: 150000,
    created_at: new Date(Date.now() - 172800000).toISOString(),
    updated_at: new Date().toISOString(),
    description: 'Cyclone season monitoring for northern Australia',
    response_agencies: ['BOM', 'Emergency Management Australia'],
    isActive: true,
    hasActiveAlerts: false
  }
];

// Resource deployment markers
export const resourceData: ResourceMarker[] = [
  {
    id: 'r1',
    type: 'command_center',
    name: 'UN OCHA Coordination Hub',
    organization: 'UN OCHA',
    latitude: 50.4501,
    longitude: 30.5234,
    capacity: 200,
    status: 'operational',
    services: ['Coordination', 'Information Management', 'Communications'],
    contact: 'ukraine.coord@un.org'
  },
  {
    id: 'r2',
    type: 'hospital',
    name: 'MSF Emergency Hospital',
    organization: 'Médecins Sans Frontières',
    latitude: 31.3547,
    longitude: 34.3088,
    capacity: 150,
    status: 'operational',
    services: ['Emergency Surgery', 'Trauma Care', 'Pediatrics'],
    contact: '+972-8-123-4567'
  },
  {
    id: 'r3',
    type: 'aid_station',
    name: 'WFP Food Distribution Center',
    organization: 'World Food Programme',
    latitude: 2.0469,
    longitude: 45.3182,
    capacity: 5000,
    status: 'operational',
    services: ['Food Distribution', 'Nutrition Screening', 'Cash Transfers']
  },
  {
    id: 'r4',
    type: 'shelter',
    name: 'NRC Transit Center',
    organization: 'Norwegian Refugee Council',
    latitude: 21.4272,
    longitude: 92.0058,
    capacity: 800,
    status: 'operational',
    services: ['Temporary Shelter', 'WASH Facilities', 'Legal Aid']
  },
  {
    id: 'r5',
    type: 'distribution_point',
    name: 'UNICEF Supplies Hub',
    organization: 'UNICEF',
    latitude: 15.5007,
    longitude: 32.5599,
    capacity: 3000,
    status: 'setup',
    services: ['Child Protection Supplies', 'Education Materials', 'Health Kits']
  }
];

// Earthquake data
export const earthquakeData: EarthquakeData[] = [
  {
    id: 'eq1',
    magnitude: 7.8,
    location: 'Kahramanmaraş Province, Turkey',
    coordinates: [36.9371, 37.5858],
    time: '2023-02-06T04:17:00Z',
    depth: 17.9,
    tsunami: false
  },
  {
    id: 'eq2',
    magnitude: 6.2,
    location: 'off the east coast of Honshu, Japan',
    coordinates: [142.369, 37.916],
    time: new Date(Date.now() - 3600000).toISOString(),
    depth: 44,
    tsunami: false
  },
  {
    id: 'eq3',
    magnitude: 5.8,
    location: 'Southern California',
    coordinates: [-118.2437, 34.0522],
    time: new Date(Date.now() - 7200000).toISOString(),
    depth: 12,
    tsunami: false
  },
  {
    id: 'eq4',
    magnitude: 6.8,
    location: 'High Atlas Mountains, Morocco',
    coordinates: [-7.9811, 31.6295],
    time: '2023-09-08T23:11:00Z',
    depth: 18.5,
    tsunami: false
  },
  {
    id: 'eq5',
    magnitude: 5.2,
    location: 'Central Chile',
    coordinates: [-70.6693, -33.4489],
    time: new Date(Date.now() - 14400000).toISOString(),
    depth: 35,
    tsunami: false
  }
];

// Affected areas (polygons)
export const affectedAreasData: AffectedArea[] = [
  {
    id: 'aa1',
    crisisId: '8', // Pakistan Floods
    coordinates: [[
      [67.0011, 24.8607],
      [68.5, 25.5],
      [69.5, 24.8],
      [68.8, 23.5],
      [67.0011, 24.8607]
    ]],
    severity: 4,
    populationCount: 2500000,
    evacuated: true,
    accessRestricted: false,
    updatedAt: new Date().toISOString()
  },
  {
    id: 'aa2',
    crisisId: '13', // Philippines Typhoon
    coordinates: [[
      [120.9842, 14.5995],
      [122.5, 15.8],
      [121.8, 16.5],
      [120.2, 15.2],
      [120.9842, 14.5995]
    ]],
    severity: 3,
    populationCount: 1800000,
    evacuated: false,
    accessRestricted: false,
    updatedAt: new Date().toISOString()
  }
];

// Crisis statistics
export const crisisStatistics = {
  totalCrises: crisisData.length,
  activeCrises: crisisData.filter(c => c.status === 'active' || c.status === 'emerging').length,
  criticalCrises: crisisData.filter(c => c.severity === 5).length,
  totalAffectedPopulation: crisisData.reduce((sum, c) => sum + c.affected_population, 0),
  resourcePoints: resourceData.length,
  lastUpdated: new Date().toISOString()
};